import { put, takeLatest, all, select } from "redux-saga/effects";
import axios from "axios";
import * as API from "../utils/api";
import {
  createForm,
  setFormData,
  getProductMaterialData,
  setProductMaterialData,
  getFormById,
  setFormById,
  dataUpdate,
  sectionSaveStart,
  sectionSaveSuccess,
  sectionSaveError,
  sectionNoteSaveStart,
  sectionNoteSaveSuccess,
  sectionNoteSaveError,
  sectionNoteWithdrawStart,
  sectionNoteWithdrawSuccess,
  sectionNoteWithdrawError,
  uploadFile,
  uploadFileStart,
  uploadFileSuccess,
  uploadFileError,
} from "../reducers/collaborativeForm.reducer";
import { logout } from "../reducers/login.reducer";
import {
  selectAuthToken,
  selectOrganisationId,
} from "../selectors/login.selector";
import {
  formDataSubmit,
  formDataSubmitSuccess,
  clearTraderOperation,
} from "../reducers/form.reducer";

function* handleFormCreation(action) {
  const { supplierId, productId, materialId } = action.payload;

  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const requestURL = API.PRODUCTS.CREATE_AND_GET_PRODUCT.replace(
    "<ORGANISATION_ID>",
    organisationId
  ).replace("<SUPPLIER_ID>", supplierId);

  const payload = {
    product: productId,
    material: materialId,
  };

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.post(requestURL, payload, { headers });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(setFormData(response.data.results));
      }
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* handleGetProductMaterialData(action) {
  const { supplierId } = action.payload;

  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);

  const requestURL = API.PRODUCTS.CREATE_AND_GET_PRODUCT.replace(
    "<ORGANISATION_ID>",
    organisationId
  ).replace("<SUPPLIER_ID>", supplierId);

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.get(requestURL, { headers });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(setProductMaterialData(response.data.results));
      }
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* handleGetFormById(action) {
  const { formId } = action.payload;

  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);

  const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
    "<FORM_ID>",
    formId
  );

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.get(requestURL, { headers });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        const formData = response?.data?.results[0];
        if (formData) {
          yield put(setFormById(formData));
        }
      }
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* handleDataUpdate(action) {
  const {
    formId,
    sectionId,
    sectionKey,
    sectionData,
    filledFields,
    userId,
    organisationId,
    timestamp,
  } = action.payload;

  try {
    const authToken = yield select(selectAuthToken);
    const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
      "<FORM_ID>",
      formId
    );

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const body = {
      sectionId,
      sectionKey,
      sectionData,
      filledFields, // Include filledFields for backend completion calculation
      userId,
      organisationId,
      timestamp,
      autoSave: true,
    };

    // Dispatch auto-save startf action
    yield put(sectionSaveStart({ sectionId }));

    const response = yield axios.put(requestURL, body, { headers });
    if (response && response.status === 200 && response.data) {

      // Dispatch auto-save success action
      yield put(
        sectionSaveSuccess({
          sectionId,
          timestamp: new Date().toISOString(),
          response: response.data,
        })
      );

      // Optionally update form data in store if response contains updated data
      yield put(setFormData(response.data.results[0]));
    }
  } catch (error) {

    // Dispatch auto-save failure action
    yield put(
      sectionSaveError({
        sectionId,
        error: error.message || "Auto-save failed",
      })
    );

    // Handle authentication errors
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* handleSectionNoteSave(action) {

  const {
    formId,
    sectionId,
    sectionKey,
    userId,
    organisationId,
    note,
    timestamp,
    poResponse,
  } = action.payload;
  try {

    const authToken = yield select(selectAuthToken);
    const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
      "<FORM_ID>",
      formId
    );

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const body = {
      sectionId,
      sectionKey,
      userId,
      organisationId,
      note,
      timestamp,
      poResponse,
      status: "submitted_with_note",
    };

    // Remove this line to prevent infinite loop
    // yield put(sectionNoteSaveStart({ sectionId }));

    const response = yield axios.patch(requestURL, body, { headers });
    if (response && response.status === 200 && response.data) {

      // Dispatch section note save success action
      yield put(
        sectionNoteSaveSuccess({
          sectionId,
          note,
          savedAt: new Date().toISOString(),
          response: response.data,
        })
      );

      // Optionally update form data in store if response contains updated data

      yield put(setFormData(response.data.results[0]));
    }
  } catch (error) {

    // Dispatch section note save failure action
    yield put(
      sectionNoteSaveError({
        sectionId,
        error: error.message || "Section note save failed",
      })
    );

    // Handle authentication errors
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* handleSectionNoteWithdraw(action) {
  const {
    formId,
    sectionId,
    sectionKey,
    userId,
    organisationId,
  } = action.payload;
  try {
    const authToken = yield select(selectAuthToken);
    const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
      "<FORM_ID>",
      formId
    );

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    // Send empty note and clear status to withdraw the request
    const body = {
      sectionId,
      sectionKey,
      userId,
      organisationId,
      note: "",
      poResponse: "",
      status: "", // Clear the status to reset section
    };

    const response = yield axios.patch(requestURL, body, { headers });
    if (response && response.status === 200 && response.data && response.data.success) {
      // Dispatch section note withdraw success action
      yield put(
        sectionNoteWithdrawSuccess({
          sectionId,
          response: response.data,
        })
      );

      // Update form data in store with the updated form
      yield put(setFormData(response.data.results[0]));
    }
  } catch (error) {
    // Dispatch section note withdraw failure action
    yield put(
      sectionNoteWithdrawError({
        sectionId,
        error: error.message || "Section note withdraw failed",
      })
    );

    // Handle authentication errors
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* handleFormDataSubmit(action) {
  const { formId, payload, isFormSubmission } = action.payload;

  const authToken = yield select(selectAuthToken);
  const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
    "<FORM_ID>",
    formId
  );

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  // Use the form data from the payload instead of empty body
  const body = { ...payload, isFormSubmission } || { isFormSubmission };

  try {
    const response = yield axios.post(requestURL, body, { headers });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(setFormData(response.data.results[0]));
        yield put(formDataSubmitSuccess(response.data.results));
        // Clear trader operation state after form data is updated
        yield put(clearTraderOperation());
      }
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
    // Clear trader operation state on error
    yield put(clearTraderOperation());
  }
}

function* handleUploadFile(action) {
  const { formId, uniqueKey, file, uploadContext } = action.payload;
  try {
    const authToken = yield select(selectAuthToken);
    const requestURL = API.PRODUCTS.UPLOAD_FILE.replace(
      "<FORM_ID>",
      formId
    ).replace("<SECTION_ID>", uniqueKey);

    // Dispatch upload start action
    if (uploadContext && uploadContext.fieldId) {
      yield put(
        uploadFileStart({
          fieldId: uploadContext.fieldId,
          sectionKey: uploadContext.sectionKey,
        })
      );
    }
    // Extract certificate type, field ID, and option from upload context
    let certificateType = null;
    let fieldId = null;
    let option = null;
    
    if (uploadContext && uploadContext.fieldId) {
      // Extract certificate type from field ID (e.g., "certificate_GOTS_certificateUpload" -> "GOTS")
      if (uploadContext.fieldId.includes('certificate_') && uploadContext.fieldId.includes('_')) {
        const parts = uploadContext.fieldId.split('_');
        if (parts.length >= 2) {
          certificateType = parts[1]; // Extract the certificate type (e.g., "GOTS")
          fieldId = uploadContext.fieldId; // Use the full field ID
        }
      }
    }
    
    // Extract option for clean chemistry uploads
    if (uploadContext && uploadContext.option) {
      option = uploadContext.option;
    }

    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: authToken,
      fileType: action?.payload?.fileType || "document",
      ...(certificateType && { certificateType }), // Add certificate type header if available
      ...(fieldId && { fieldId }), // Add field ID header if available
      ...(option && { option }), // Add option header for clean chemistry uploads
    };
    let uploadedFiles = [];

    for (let fileItem of file) {
      const formData = new FormData();
      formData.append("file", fileItem.file);
      formData.append("fileType", action?.payload?.fileType || "document");

      // For FormData, don't set Content-Type manually - let the browser set it

      try {
        const response = yield axios.put(requestURL, formData, {
          headers,
        });

        if (response && response.status === 200 && response.data) {
          if (response.data.success) {
            yield put(setFormData(response.data.results[0]));
            uploadedFiles.push({
              id: fileItem.id,
              name: fileItem.name,
              data: response.data.results,
            });
          }
        }
      } catch (error) {
        if (error && error.response && error.response.status === 401) {
          yield put(logout());
        }

        // Dispatch upload error action
        if (uploadContext && uploadContext.fieldId) {
          yield put(
            uploadFileError({
              fieldId: uploadContext.fieldId,
              error: error.message || "File upload failed",
            })
          );
        }
        return;
      }
    }

    // Dispatch upload success action
    if (uploadContext && uploadContext.fieldId && uploadedFiles.length > 0) {
      yield put(
        uploadFileSuccess({
          fieldId: uploadContext.fieldId,
          sectionKey: uploadContext.sectionKey,
          fileData: uploadedFiles,
        })
      );
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }

    // Dispatch upload error action
    if (uploadContext && uploadContext.fieldId) {
      yield put(
        uploadFileError({
          fieldId: uploadContext.fieldId,
          error: error.message || "File upload failed",
        })
      );
    }
  }
}

function* watchCreateProductSaga() {
  yield takeLatest(createForm.type, handleFormCreation);
}

function* watchGetProductMaterialDataSaga() {
  yield takeLatest(getProductMaterialData.type, handleGetProductMaterialData);
}

function* watchGetFormByIdSaga() {
  yield takeLatest(getFormById.type, handleGetFormById);
}

function* watchDataUpdateSaga() {
  yield takeLatest(dataUpdate.type, handleDataUpdate);
}

function* watchSectionNoteSaveSaga() {
  yield takeLatest(sectionNoteSaveStart.type, handleSectionNoteSave);
}

function* watchSectionNoteWithdrawSaga() {
  yield takeLatest(sectionNoteWithdrawStart.type, handleSectionNoteWithdraw);
}

function* watchFormDataSubmitSaga() {
  yield takeLatest(formDataSubmit.type, handleFormDataSubmit);
}

function* watchUploadFileSaga() {
  yield takeLatest(uploadFile.type, handleUploadFile);
}

export default function* rootSaga() {
  yield all([
    watchCreateProductSaga(),
    watchGetProductMaterialDataSaga(),
    watchGetFormByIdSaga(),
    watchDataUpdateSaga(),
    watchSectionNoteSaveSaga(),
    watchSectionNoteWithdrawSaga(),
    watchFormDataSubmitSaga(),
    watchUploadFileSaga(),
  ]);
}
